import { Check } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { PACKAGES } from "@/data/content";
import { useAnchorNav } from "@/lib/nav";

const PackageCard = ({ p, delay }) => {
  const go = useAnchorNav();
  return (
    <Reveal delay={delay} className="h-full">
      <article
        data-testid={p.testId}
        className={`relative flex h-full flex-col rounded-2xl border p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 sm:p-7 ${
          p.popular
            ? "border-orange bg-navy shadow-2xl shadow-orange/10"
            : "border-white/10 bg-navy hover:border-teal/60"
        }`}
      >
        {p.popular && (
          <span className="absolute -top-3.5 left-6 rounded-full bg-orange px-3 py-1 font-display text-xs font-bold uppercase tracking-wide text-navy">
            Populärt val
          </span>
        )}
        <h3 className="font-display text-xl font-bold text-white">{p.name}</h3>
        <p className="mt-1.5 min-h-10 text-sm text-cream/60">{p.tagline}</p>
        <div className="mt-5 flex items-end gap-2">
          <span className="font-display text-3xl font-extrabold tracking-tight text-white">{p.oneTime}</span>
          <span className="pb-1 text-xs text-cream/50">engångspris</span>
        </div>
        {p.monthly && <p className="mt-1 font-display text-sm font-bold text-orange">+ {p.monthly}</p>}
        <ul className="mt-6 flex-1 space-y-2.5">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-cream/80">
              <Check size={16} className="mt-0.5 shrink-0 text-orange2" aria-hidden="true" />
              {f}
            </li>
          ))}
        </ul>
        <a
          href="#kontakt"
          data-testid={`${p.testId}-cta`}
          onClick={(e) => go(e, "kontakt")}
          className={`mt-7 inline-flex items-center justify-center rounded-lg px-5 py-3 font-display text-sm font-bold transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${
            p.popular
              ? "bg-orange text-navy shadow-[0_4px_14px_rgba(255,125,0,0.3)] hover:bg-orange2"
              : "bg-teal text-white hover:bg-slate2"
          }`}
        >
          Välj paket
        </a>
      </article>
    </Reveal>
  );
};

const Packages = () => {
  const go = useAnchorNav();
  const regular = PACKAGES.filter((p) => !p.wide);
  const complete = PACKAGES.find((p) => p.wide);

  return (
    <section id="paket" className="bg-navy2 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>Paket</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Paket för varje steg i din digitala resa
          </h2>
          <p className="mt-4 text-base text-cream/60 sm:text-lg">
            Tydliga paket med fast engångspris och låg månadskostnad – välj det som passar ditt företag bäst.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {regular.map((p, i) => (
            <PackageCard key={p.id} p={p} delay={(i % 3) * 0.08} />
          ))}
        </div>

        <Reveal delay={0.1}>
          <article
            data-testid={complete.testId}
            className="mt-6 grid gap-8 rounded-2xl border border-teal/50 bg-navy p-7 sm:p-9 lg:grid-cols-2"
          >
            <div>
              <h3 className="font-display text-2xl font-bold text-white">{complete.name}</h3>
              <p className="mt-1.5 text-sm text-cream/60">{complete.tagline}</p>
              <div className="mt-6 flex items-end gap-2">
                <span className="font-display text-4xl font-extrabold tracking-tight text-white">{complete.oneTime}</span>
                <span className="pb-1.5 text-xs text-cream/50">engångspris</span>
              </div>
              {complete.monthly && (
                <p className="mt-1 font-display text-sm font-bold text-orange">+ {complete.monthly}</p>
              )}
              <a
                href="#kontakt"
                data-testid={`${complete.testId}-cta`}
                onClick={(e) => go(e, "kontakt")}
                className="mt-8 inline-flex items-center justify-center rounded-lg bg-orange px-6 py-3.5 font-display text-sm font-bold text-navy shadow-[0_4px_14px_rgba(255,125,0,0.3)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange2"
              >
                Välj paket
              </a>
            </div>
            <ul className="grid content-center gap-2.5 sm:grid-cols-2">
              {complete.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-cream/80">
                  <Check size={16} className="mt-0.5 shrink-0 text-orange2" aria-hidden="true" />
                  {f}
                </li>
              ))}
            </ul>
          </article>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-8 text-center text-sm text-cream/50">
            Google- och Meta-annonskostnader ingår inte i priserna.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Packages;
